import {useEffect, useMemo, useRef, useState} from 'react';
import {cloudinary} from '../../../../../../assets/images/cloudinary';
import {useFreeTierHook} from '../../../../../../hooks/free-tier';
import {ArrowHeadDownIcon, PlusIcon} from '../../../../../../ui/atoms/icons';
import {MagnifyingGlassIcon} from '../../../../../../ui/atoms/icons/magnifying-glass';
import {TextField} from '../../../../../../ui/molecules/field/textfield';
import {MultiUserSelectField} from '../../../../../../ui/molecules/select/multi-user';
import {UserSelectField} from '../../../../../../ui/molecules/select/user';
import FreeTierModal from '../../../../../../ui/organisms/free-tier/free-tier-modal';
import {
  DialogHeader,
  DialogOverlay,
  MemberPrimaryButton,
  SecondaryButton,
} from '../admin-member/member-redesign.styles';
import {
  CreateGroupBody,
  CreateGroupCustomAction,
  CreateGroupCustomIcon,
  CreateGroupDialog as StyledCreateGroupDialog,
  CreateGroupField,
  CreateGroupFooter,
  CreateGroupHeading,
  CreateGroupSubtitle,
  CreateGroupTitle,
  CreateGroupTypeButton,
  CreateGroupTypeDropdown,
  CreateGroupTypeError,
  CreateGroupTypeLabel,
  CreateGroupTypeList,
  CreateGroupTypeOptionButton,
  CreateGroupTypeSearch,
  CreateGroupTypeSearchIcon,
  CreateGroupTypeWrapper,
} from './admin-group-style';
import {
  CreateGroupTypeOption,
  useCreateGroupDialog,
} from './create-group-dialog-hooks';

type CreateGroupDialogProps = {
  groupTypes: CreateGroupTypeOption[];
  members: any[];
  onClose: () => void;
  onCreated: () => Promise<void> | void;
};

const userName = (member: any) => {
  const name = [member?.firstName, member?.lastName].filter(Boolean).join(' ');
  return name || `${member?.email || 'Unnamed member'} (pending invitation)`;
};

export const CreateGroupDialog = ({
  groupTypes,
  members,
  onClose,
  onCreated,
}: CreateGroupDialogProps) => {
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const {
    isAccountFreeTier,
    showFreeTierModal,
    handleShowFreeTierModal,
  } = useFreeTierHook();
  const {
    canSubmit,
    close,
    customTypeName,
    errors,
    groupAdmin,
    groupTypeId,
    isCustomType,
    isSubmitting,
    name,
    selectCustomType,
    selectExistingType,
    setCustomTypeName,
    setGroupAdmin,
    setName,
    setUsers,
    submit,
    users,
  } = useCreateGroupDialog({groupTypes, onClose, onCreated});

  const memberOptions = useMemo(
    () =>
      members.map((member) => ({
        label: {
          avatar: {
            name: userName(member),
            src: member?.avatar?.url || '',
          },
          id: member.id,
          name: userName(member),
        },
        value: member.id,
      })),
    [members],
  );

  const selectedType = groupTypes.find((type) => type.id === groupTypeId);
  const filteredTypes = useMemo(() => {
    const query = typeSearch.trim().toLowerCase();
    if (!query) return groupTypes;
    return groupTypes.filter((type) =>
      type.name.toLowerCase().includes(query),
    );
  }, [groupTypes, typeSearch]);

  useEffect(() => {
    const closeDropdown = (event: MouseEvent) => {
      if (!typeDropdownRef.current?.contains(event.target as Node)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', closeDropdown);
    return () => document.removeEventListener('mousedown', closeDropdown);
  }, []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (typeDropdownOpen) {
        setTypeDropdownOpen(false);
        return;
      }
      close();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [close, typeDropdownOpen]);

  return (
    <DialogOverlay
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}>
      <StyledCreateGroupDialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-group-title"
        onMouseDown={(event) => event.stopPropagation()}>
        <form
          method="post"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}>
          <DialogHeader>
            <CreateGroupHeading>
              <CreateGroupTitle id="create-group-title">
                Create group
              </CreateGroupTitle>
              <CreateGroupSubtitle>
                Set up a group and choose its lead and members.
              </CreateGroupSubtitle>
            </CreateGroupHeading>
            <SecondaryButton
              type="button"
              disabled={isSubmitting}
              onClick={close}>
              ×&nbsp;&nbsp;Close
            </SecondaryButton>
          </DialogHeader>

          <CreateGroupBody>
            <CreateGroupField>
              <TextField
                autoFocus
                label="Group name"
                placeholder="Enter group name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                helper={errors.name || ''}
                state={errors.name ? 'error' : 'default'}
              />
            </CreateGroupField>

            <CreateGroupField>
              <UserSelectField
                name="groupAdmin"
                value={groupAdmin}
                helper={errors.groupAdmin || ''}
                state={errors.groupAdmin ? 'error' : 'default'}
                onChange={(option: any) => setGroupAdmin(option.value)}
                label="Group lead"
                placeholder="Select a member"
                options={memberOptions}
              />
            </CreateGroupField>

            <CreateGroupField>
              <MultiUserSelectField
                name="users"
                value={users}
                onChange={(options: any[]) =>
                  setUsers(options.map((option) => option.value))
                }
                label="Add team members"
                placeholder="Select members"
                options={memberOptions.filter(
                  (option) => option.value !== groupAdmin,
                )}
              />
            </CreateGroupField>

            <CreateGroupTypeWrapper ref={typeDropdownRef}>
              <CreateGroupTypeLabel>Type</CreateGroupTypeLabel>
              <CreateGroupTypeButton
                type="button"
                aria-haspopup="listbox"
                aria-expanded={typeDropdownOpen}
                $error={Boolean(errors.groupType)}
                $placeholder={!isCustomType && !selectedType}
                onClick={() => setTypeDropdownOpen((open) => !open)}>
                <span>
                  {isCustomType
                    ? 'Custom'
                    : selectedType?.name || 'Select group type'}
                </span>
                <ArrowHeadDownIcon
                  style={{
                    transform: typeDropdownOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              </CreateGroupTypeButton>
              {errors.groupType && (
                <CreateGroupTypeError>{errors.groupType}</CreateGroupTypeError>
              )}

              {typeDropdownOpen && (
                <CreateGroupTypeDropdown>
                  <CreateGroupTypeSearch>
                    <input
                      autoFocus
                      aria-label="Search group types"
                      placeholder="Search"
                      value={typeSearch}
                      onChange={(event) => setTypeSearch(event.target.value)}
                    />
                    <CreateGroupTypeSearchIcon aria-hidden="true">
                      <MagnifyingGlassIcon />
                    </CreateGroupTypeSearchIcon>
                  </CreateGroupTypeSearch>
                  <CreateGroupTypeList role="listbox">
                    {filteredTypes.map((type) => (
                      <CreateGroupTypeOptionButton
                        key={type.id}
                        type="button"
                        role="option"
                        aria-selected={type.id === groupTypeId}
                        $selected={type.id === groupTypeId}
                        onClick={() => {
                          selectExistingType(type.id);
                          setTypeDropdownOpen(false);
                          setTypeSearch('');
                        }}>
                        {type.name}
                      </CreateGroupTypeOptionButton>
                    ))}
                    {filteredTypes.length === 0 && (
                      <CreateGroupTypeError>
                        No matching group types
                      </CreateGroupTypeError>
                    )}
                  </CreateGroupTypeList>
                  <CreateGroupCustomAction
                    type="button"
                    onClick={() => {
                      if (isAccountFreeTier) {
                        setTypeDropdownOpen(false);
                        handleShowFreeTierModal(true);
                        return;
                      }
                      selectCustomType();
                      setTypeDropdownOpen(false);
                      setTypeSearch('');
                    }}>
                    <CreateGroupCustomIcon>
                      <PlusIcon />
                    </CreateGroupCustomIcon>
                    Create custom group type
                  </CreateGroupCustomAction>
                </CreateGroupTypeDropdown>
              )}
            </CreateGroupTypeWrapper>

            {isCustomType && (
              <CreateGroupField>
                <TextField
                  aria-label="Custom group type name"
                  placeholder="Enter custom group type"
                  value={customTypeName}
                  onChange={(event) => setCustomTypeName(event.target.value)}
                  helper={errors.customTypeName || ''}
                  state={errors.customTypeName ? 'error' : 'default'}
                />
              </CreateGroupField>
            )}
          </CreateGroupBody>

          <CreateGroupFooter>
            <SecondaryButton
              type="button"
              disabled={isSubmitting}
              onClick={close}>
              Cancel
            </SecondaryButton>
            <MemberPrimaryButton
              type="submit"
              disabled={!canSubmit || isSubmitting}
              data-form-action={true}
              aria-busy={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create group'}
            </MemberPrimaryButton>
          </CreateGroupFooter>
        </form>
      </StyledCreateGroupDialog>
      <FreeTierModal
        open={showFreeTierModal}
        onClose={() => handleShowFreeTierModal(false)}
        image={{
          src: cloudinary.sortImage,
          extension: 'png',
          variants: [
            {src: cloudinary.sortImage, size: '1x'},
            {src: cloudinary.sortImage2x, size: '2x'},
            {src: cloudinary.sortImage3x, size: '3x'},
          ],
        }}
        title="Use custom group types for ultimate flexibility"
        description="Upgrade your plan to create a group type that matches your workspace structure."
      />
    </DialogOverlay>
  );
};
